import { IKeyValue } from "helpers/url";

import { IApiClient, IKeyAny, IRequestResult } from "../api-client";

export type Service =
  | "rest-api"
  | "video"
  | "global-accounts"
  | "livechat"
  | "knowledgebase"
  | "dev-platform"
  | "accounts-billing";

export default abstract class BaseApi {
  private readonly api: IApiClient;
  protected readonly service: Service = "rest-api";
  protected readonly prefix: string = "";
  protected readonly version: number = 1;

  constructor(api: IApiClient) {
    this.api = api;
  }

  private getPath(path: string, prefixOverride?: string): string {
    const versionPrefix = this.service === "livechat" || this.service === "knowledgebase" ? "" : `v${this.version}`;
    const prefix = prefixOverride || this.prefix || "";

    return [versionPrefix, prefix, path].filter(Boolean).join("/");
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  private combineHeaders(headers: any) {
    return headers;
  }

  protected getCurrentAgentId(): string {
    return this.api.getCurrentAgentId ? this.api.getCurrentAgentId() : "";
  }

  protected getRequestOptions(): IKeyValue {
    if (this.service === "global-accounts") {
      return { globalAccounts: true };
    }

    if (this.service === "video") {
      return { video: true };
    }

    if (this.service === "livechat") {
      return { livechat: true };
    }

    if (this.service === "knowledgebase") {
      return { knowledgebase: true };
    }

    if (this.service === "dev-platform") {
      return { devPlatform: true };
    }

    if (this.service === "accounts-billing") {
      return { accountsBilling: true };
    }

    return {};
  }

  protected get<TResult>(
    path = "",
    params?: IKeyValue,
    headers?: IKeyValue,
    prefixOverride?: string
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);
    const options = this.getRequestOptions();

    const args = [combinedPath, params, combinedHeaders, options];
    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.get.call(this.api, ...args);
  }

  protected post<TResult>(
    path = "",
    body?: IKeyAny,
    headers?: IKeyValue,
    prefixOverride?: string
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);
    const options = this.getRequestOptions();

    const args = [combinedPath, body, combinedHeaders, options];
    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.post.call(this.api, ...args);
  }

  protected postWithProgress<TResult>(
    path = "",
    body?: IKeyAny,
    headers?: IKeyValue,
    prefixOverride?: string,
    onProgress?: (event: ProgressEvent) => void,
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    xhrCallback?: (xhr: XMLHttpRequest, body: any) => void
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const args = [combinedPath, body, combinedHeaders, onProgress || (() => {}), xhrCallback];

    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.postWithProgress.call(this.api, ...args);
  }

  protected patch<TResult>(
    path = "",
    body?: IKeyAny,
    headers?: IKeyValue,
    prefixOverride?: string
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);
    const options = this.getRequestOptions();

    const args = [combinedPath, body, combinedHeaders, options];
    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.patch.call(this.api, ...args);
  }

  protected delete<TResult>(
    path = "",
    body?: IKeyAny,
    headers?: IKeyValue,
    prefixOverride?: string
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);
    const options = this.getRequestOptions();

    const args = [combinedPath, body, combinedHeaders, options];
    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.delete.call(this.api, ...args);
  }

  protected put<TResult>(
    path = "",
    body?: IKeyAny,
    headers?: IKeyValue,
    prefixOverride?: string
  ): Promise<IRequestResult<TResult>> {
    const combinedPath = this.getPath(path, prefixOverride);
    const combinedHeaders = this.combineHeaders(headers);
    const options = this.getRequestOptions();

    const args = [combinedPath, body, combinedHeaders, options];
    // @ts-expect-error TS(2322): Type 'Promise<IRequestResult<unknown>>' is not ass... Remove this comment to see the full error message
    return this.api.put.call(this.api, ...args);
  }
}
